import React, { useEffect, useState } from 'react';
import {
  IonAlert,
  IonButton,
  IonButtons,
  IonContent,
  IonGrid,
  IonPage,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonRow,
  IonTextarea,
  IonToolbar,
  IonTitle,
} from '@ionic/react';
import { 
  car,
  checkmark, 
  close, 
  hand,
  home, 
  list, 
  checkboxOutline, 
  pause, 
  play 
} from 'ionicons/icons';
import {
  getJob,
  getJobChecklists,
  getJobFlags,
  getJobNotes,
  getProperty,
  getSubmittedNotes,
  updateJobFlags
} from '../utilities/Storage';
import { addUpdate } from '../utilities/Updates';
import ActionButton from '../components/ActionButton';
import moment from 'moment';
import {Toolbar} from "./Toolbar";

export default function Job({history, match}) {

  const id = match.params.id;

  const [job, setJob] = useState();
  const [property, setProperty] = useState();
  const [jobFlags, setJobFlags] = useState();
  const [notesCount, setNotesCount] = useState(0);
  const [checklistsCount, setChecklistsCount] = useState(0);
  const [riskAlert, setRiskAlert] = useState(false);
  const [association, setAssociation] = useState(false);

  const [cannotStartModal, setCannotStartModal] = useState(false);
  const [cannotStartNoteText, setCannotStartNoteText] = useState('');
  const [cannotCompleteModal, setCannotCompleteModal] = useState(false);
  const [cannotCompleteNoteText, setCannotCompleteNoteText] = useState('');

  const [startedDateTime, setStartedDateTime] = useState();
  const [startedTimer, setStartedTimer] = useState(0);
  const [pausedDateTime, setPausedDateTime] = useState();
  const [pausedTimer, setPausedTimer] = useState(0);
  const [completedDateTime, setCompletedDateTime] = useState();
  const [totalDurationTimer, setTotalDurationTimer] = useState(0);

  useEffect(() => {

    const getData = async () => {
      const currentUser = JSON.parse(localStorage.apiuser || "{}");
      const j = await getJob(id);
      setJob(j);

      if (j?.property?.id) {
        setProperty(j.property);
      } else {
        const propertyId = j.property.substr(j.property.lastIndexOf("/") + 1);
        const p = await getProperty(propertyId);

        setProperty(p);
      }

      const jn = await getJobNotes(id);
      let nc = jn.length;

      const sn = await getSubmittedNotes(id);

      for (const note of sn) {
        const find = jn.findIndex(n => {
          let noteText = n.note.notetexts.map(noteText => {return noteText.notetext}).join("\n");
          return note.noteText === noteText;
        });

        if (find === -1) {
          nc += 1;
        }
      }

      setNotesCount(nc);

      const jf = await getJobFlags(id);

      setJobFlags(getJobFlagsFromJob(j));

      if (Object.keys(jf).length > 0) {
        // if there are job flags stored then consider them more recent than the
        // cached job, because job flags are removed when the job is cached
        setJobFlags(jf);

        if (jf['started_updated']) {
          setStartedDateTime(jf['started_updated']);
        }
        if (jf['paused_updated']) {
          setPausedDateTime(jf['paused_updated']);
        }
        if (jf['completed_updated']) {
          setCompletedDateTime(jf['completed_updated']);
        }
      }

      const cl = await getJobChecklists(id);

      setChecklistsCount(cl?.length);

      const user = JSON.parse(localStorage.apiuser);

      setAssociation(user.id !== j.workordersupplier.supplier.id);
    }

    getData();

    history.listen(val => {
      if (val.pathname === '/job/' + id) {
        getData();
      }
    });

  }, []);

  useEffect(() => {
    if (!jobFlags) {
      return;
    }

    if (jobFlags.started) {
      setStartedTimer(moment().diff(startedDateTime, 'seconds', true));
    } else {
      setStartedTimer(0);
    }

    if (jobFlags.paused) {
      setPausedTimer(moment().diff(pausedDateTime, 'seconds', true));
    } else {
      setPausedTimer(0);
    }

    if (jobFlags.started && jobFlags.waspaused) {
      // job has been 'paused' at some point, so show a total duration timer
      const sorted = [...jobFlags.history].sort((a, b) => moment(a.updated) > moment(b.updated) ? 1 : -1);
      let started = undefined;

      sorted.forEach((item, i) => {
        if (item.update === 'started' && !started) {
          started = moment(item.updated);
        }
        if (item.update === 'paused' && started) {
          if (sorted[i + 1]) {
            const seconds = moment(sorted[i + 1].updated).diff(moment(item.updated), 'seconds', true);
            started.add(seconds, 'seconds');
          } else {
            const seconds = moment().diff(moment(item.updated), 'seconds', true);
            started.add(seconds, 'seconds');
          }
        }
      });

      setTotalDurationTimer(moment().diff(started, 'seconds', true));
    } else {
      setTotalDurationTimer(0);
    }

  }, [jobFlags, startedDateTime, pausedDateTime]);

  useEffect(() => {
    if (!startedTimer) {
      return;
    }

    const intervalId = setInterval(() => {
      setStartedTimer(startedTimer + 1);
    }, 1000);

    return () => clearInterval(intervalId);
  }, [startedTimer]);

  useEffect(() => {
    if (!pausedTimer) {
      return;
    }

    const intervalId = setInterval(() => {
      setPausedTimer(pausedTimer + 1);
    }, 1000);

    return () => clearInterval(intervalId);
  }, [pausedTimer]);

  useEffect(() => {
    if (!totalDurationTimer) {
      return;
    }

    const intervalId = setInterval(() => {
      setTotalDurationTimer(totalDurationTimer + 1);
    }, 1000);

    return () => clearInterval(intervalId);
  }, [totalDurationTimer]);

  const getJobFlagsFromJob = (job) => {
    let jf = {
      started: false,
      paused: false,
      completed: false,
      cannotcomplete: false,
      cannotstart: false,
      waspaused: false,
      history: [],
    };

    const completeStatuses = [
      'completed',
      'invoiced',
      'invoice approved',
      'invoice rejected',
      'owner charged',
      'supplier paid',
      'financially completed',
    ];

    const jobStatus = job.status.toLowerCase();

    if (job?.substatus?.id && job?.substatus?.workordersubstatus?.substatusreference === 'could_not_complete') {
      jf.cannotcomplete = true;
    } else if (job?.substatus?.id && job?.substatus?.workordersubstatus?.substatusreference === 'cannot_start_job') {
      jf.cannotstart = true;
    } else if (job?.substatus?.id && job?.substatus?.workordersubstatus?.substatusreference === 'started_paused') {
      jf.paused = true;
    } else if (job?.completeddate) {
      jf.completed = true;
    } else if (jobStatus === 'started') {
      jf.started = true;
    } else if (completeStatuses.includes(jobStatus)) {
      jf.completed = true;
    }

    let startedDateTime = undefined;
    let completedDateTime = undefined;
    let pausedDateTime = undefined;

    if (job?.statushistory) {
      job.statushistory.sort((a, b) => moment(a.from) > moment(b.from) ? 1 : -1).forEach(status => {
        status.substatus.sort((a, b) => moment(a.fromdatetime) > moment(b.fromdatetime) ? 1 : -1).forEach(substatus => {
          switch (substatus.workordersubstatus.substatusreference) {
            case 'started':
              startedDateTime = moment(substatus.fromdatetime);

              jf.history.push({
                update: 'started',
                updated: substatus.fromdatetime
              });
              break;
            case 'started_paused':
              pausedDateTime = moment(substatus.fromdatetime);
              jf.waspaused = true;

              jf.history.push({
                update: 'paused',
                updated: substatus.fromdatetime
              });
              break;
            case 'completed':
              completedDateTime = moment(substatus.fromdatetime);
              break;
          }
        });
      });
    }

    setStartedDateTime(startedDateTime);
    setCompletedDateTime(completedDateTime);
    setPausedDateTime(pausedDateTime);

    return jf;
  }

  const toggleCannotStartModal = () => {
    setCannotStartModal(!cannotStartModal);
  }

  const toggleCannotCompleteModal = () => {
    setCannotCompleteModal(!cannotCompleteModal);
  }

  const secondsToHms = (d) => {
    d = Number(d);
    var h = Math.floor(d / 3600);
    var m = Math.floor(d % 3600 / 60);
    var s = Math.floor(d % 3600 % 60);

    var hDisplay = h > 0 ? h + (h == 1 ? " hour, " : " hours, ") : "";
    var mDisplay = m > 0 ? m + (m == 1 ? " minute, " : " minutes, ") : "";
    var sDisplay = s > 0 ? s + (s == 1 ? " second" : " seconds") : "";
    return hDisplay + mDisplay + sDisplay;
  }

  const updateJob = (update) => {
    let now = moment();

    let obj = {
      jobId: id,
      fromdatetime: now.format('YYYY-MM-DD HH:mm:ss'),
    };

    if (['cannotstart', 'cannotcomplete'].includes(update)) {
      if (update === 'cannotstart') {
        obj.noteText = cannotStartNoteText;
        obj.noteSubject = 'Field worker cannot start job';
      } else {
        obj.noteText = cannotCompleteNoteText;
        obj.noteSubject = 'Field worker cannot complete job';
      }
    }

    addUpdate('job_' + update, obj);

    let jf = {...jobFlags};

    for (const prop in jobFlags) {
      if (['started', 'paused', 'completed', 'cannotcomplete', 'cannotstart'].includes(prop)) {
        jf[prop] = false;
      }
    }

    jf[update] = true;
    jf[update + '_updated'] = now.format('YYYY-MM-DD HH:mm:ss');

    if (!jf.history) {
      jf.history = [];
    }

    jf.history.push({
      update,
      updated: now.format('YYYY-MM-DD HH:mm:ss')
    });

    if (update === 'paused') {
      jf.waspaused = true;
    }

    updateJobFlags(job.id, jf).then(() => {
      setJobFlags(jf);
      if (update === 'started') {
        setStartedDateTime(jf[update + '_updated']);
      }
      if (update === 'paused') {
        setPausedDateTime(jf[update + '_updated']);
      }
      if (update === 'completed') {
        setCompletedDateTime(jf[update + '_updated']);
      }
    });
  }

  if (!job || !jobFlags || !property) {
    return null;
  }

  const { address } = property;

  const style = { marginRight: "10px" }
  const style2 = { whiteSpace: "normal" }

  const actionButtons = [];

  if (!association) {
    if (!Object.values(jobFlags).includes(true)) {
      actionButtons.push(
        { label: 'mark as started', icon: checkmark, onClick: () => setRiskAlert(true) }
      );
      actionButtons.push(
        { label: "can't start job", icon: close, onClick: () => toggleCannotStartModal() }
      );
    }
    if (jobFlags.started) {
      actionButtons.push(
        { label: "can't do job", icon: close, onClick: () => toggleCannotCompleteModal() }
      );
      actionButtons.push(
        { label: "job completed", icon: checkmark, onClick: () => updateJob('completed') }
      );
      actionButtons.push(
        { label: "pause job", icon: pause, onClick: () => updateJob('paused') }
      );
    }
    if (jobFlags.paused) {
      actionButtons.push(
        { label: "resume job", icon: play, onClick: () => updateJob('started') }
      );
    }
    if (jobFlags.cannotstart) {
      actionButtons.push(
        { label: 'can start job', icon: checkmark, onClick: () => setRiskAlert(true) }
      );
    }
  }

  const listItems = [
    {
      icon: car,
      label: (
        <React.Fragment>
          description
          <p style={style2}>{job.fulldescription}</p>
        </React.Fragment>
      )
    },
    {
      icon: home,
      label: (
        <React.Fragment>
          property
          <p>
            {property.name}<br />
            {address?.line1}, {address?.postcode}
          </p>
        </React.Fragment>
      ),
      onClick: () => { history.push(`/property/${property.id}`) }
    },
    {
      icon: list,
      label: (

        <React.Fragment>
          notes
          <p>{notesCount} note(s)</p>
        </React.Fragment>
      ),
      onClick: () => { history.push(`/job/${id}/notes`) }
    },
    {
      icon: checkboxOutline,
      label: (

        <React.Fragment>
          checklists
          <p>{checklistsCount} checklist(s)</p>
        </React.Fragment>
      ),
      onClick: () => { history.push(`/job/${id}/checklists`) },
      disabled: checklistsCount === 0
    }
  ];

  if (jobFlags.completed) {
    const sorted = [...jobFlags.history].sort((a, b) => moment(a.updated) > moment(b.updated) ? 1 : -1);
    let started = undefined;
    let completed = undefined;

    sorted.forEach((item, i) => {
      if (item.update === 'started' && !started) {
        started = moment(item.updated);
      }
      if (item.update === 'paused' && started) {
        if (sorted[i + 1]) {
          const seconds = moment(sorted[i + 1].updated).diff(moment(item.updated), 'seconds', true);
          started.add(seconds, 'seconds');
        } else {
          const seconds = moment().diff(moment(item.updated), 'seconds', true);
          started.add(seconds, 'seconds');
        }
      }
      if (item.update === 'completed') {
        completed = moment(item.updated);
      }
    });

    let completedDuration = undefined;

    if (started && completed) {
      const seconds = moment(completed).diff(moment(started), 'seconds', true);
      completedDuration = secondsToHms(seconds);
    }

    listItems.push({
      icon: checkmark,
      label: <React.Fragment>job complete{completedDuration ? ' - ' + completedDuration : ''}</React.Fragment>
    });
  }
  if (jobFlags.started) {
    listItems.push({ icon: checkmark, label: <React.Fragment>job is started</React.Fragment> })
  }
  if (jobFlags.paused) {
    listItems.push({ icon: pause, label: <React.Fragment>job is paused</React.Fragment> })
  }
  if (jobFlags.cannotdo) {
    listItems.push({ icon: close, label: <React.Fragment>can't do job</React.Fragment> })
  }
  if (jobFlags.cannotstart) {
    listItems.push({ icon: close, label: <React.Fragment>can't start job</React.Fragment> })
  }
  if (association) {
    listItems.push({ icon: hand, label: <React.Fragment>you are assisting with this job</React.Fragment>})
  }

  const pageTitle = `Job ${id}`;

  return (
    <IonPage>
      <IonHeader>
        <Toolbar title={pageTitle} backButtonHref="/" />
      </IonHeader>
      <IonContent>
        <h3 className="ion-padding-start">{job.shortdescription}</h3>
        {startedTimer > 0 &&
        <IonItem>
          <p>{jobFlags.waspaused ? 'resumed' : 'started'} {secondsToHms(startedTimer)} ago</p>
        </IonItem>
        }
        {pausedTimer > 0 &&
        <IonItem>
          <p>paused {secondsToHms(pausedTimer)} ago</p>
        </IonItem>
        }
        {totalDurationTimer > 0 &&
        <IonItem>
          <p>total duration {secondsToHms(totalDurationTimer)}</p>
        </IonItem>
        }
        <IonList lines="full">
          {listItems.map((li, i) =>
          <IonItem
            key={'li' + i}
            button={true}
            onClick={li.onClick}
            detail={li.onClick ? true : false}
            disabled={li.disabled}
          >
            <IonIcon icon={li.icon} size="large" style={style} />
            <IonLabel>
              {li.label}
            </IonLabel>
          </IonItem>
          )}
        </IonList>
        <IonGrid>
          <IonRow>
            {actionButtons.map((ab, i) =>
            <ActionButton key={i} {...ab} />
            )}
          </IonRow>
        </IonGrid>
      </IonContent>
      {riskAlert &&
      <IonAlert
        isOpen={riskAlert}
        onDidDismiss={() => setRiskAlert(false)}
        header={'Is it safe to proceed?'}
        message={'Is it safe to proceed with this job?'}
        buttons={[
          {
            text: 'No',
            handler: () => {
              setRiskAlert(false);
            }
          },
          {
            text: 'Yes',
            handler: () => {
              updateJob('started');
              setRiskAlert(false);
            }
          }
        ]}
      />
      }
      {cannotStartModal &&
      <IonModal isOpen>
        <IonHeader translucent>
          <IonToolbar>
            <IonTitle>Can't start job</IonTitle>
            <IonButtons slot="end">
              <IonButton onclick={toggleCannotStartModal}>Close</IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent fullscreen>
          <IonList>
            <IonItem>
              Please enter the reason you can't start the job below.
            </IonItem>
            <IonItem>
              <IonLabel position="stacked">reason</IonLabel>
              <IonTextarea
                autoGrow={true}
                onIonChange={e => { setCannotStartNoteText(e.target.value); }}
              />
            </IonItem>
          </IonList>
          <IonButton
            expand="block"
            disabled={cannotStartNoteText.length === 0}
            onClick={() => {
              updateJob('cannotstart');
              toggleCannotStartModal();
            }}
          >
            submit
          </IonButton>
        </IonContent>
      </IonModal>
      }
      {cannotCompleteModal &&
      <IonModal isOpen>
        <IonHeader translucent>
          <IonToolbar>
            <IonTitle>Can't complete job</IonTitle>
            <IonButtons slot="end">
              <IonButton onclick={toggleCannotCompleteModal}>Close</IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent fullscreen>
          <IonList>
            <IonItem>
              Please enter the reason you can't complete the job below.
            </IonItem>
            <IonItem>
              <IonLabel position="stacked">reason</IonLabel>
              <IonTextarea
                autoGrow={true}
                onIonChange={e => { setCannotCompleteNoteText(e.target.value); }}
              />
            </IonItem>
          </IonList>
          <IonButton
            expand="block"
            disabled={cannotCompleteNoteText.length === 0}
            onClick={() => {
              updateJob('cannotcomplete');
              toggleCannotCompleteModal();
            }}
          >
            submit
          </IonButton>
        </IonContent>
      </IonModal>
      }
    </IonPage>
  );

}